import { Placement } from '@iheartjane/dm-sdk';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { Box, Flex, useMobileMediaQuery } from '@iheartjane/reefer';
import { useSearchParams } from 'react-router';

import { useFacets, useFetchMultiPlacements } from '@jane/dm/internal';
import { useSearchContext } from '@jane/search/hooks';
import {
  DESKTOP_PRODUCTS_PER_PAGE_GRID,
  MOBILE_PRODUCTS_PER_PAGE_GRID,
  buildSearchFilter,
} from '@jane/search/util';
import { MenuProductCarousel } from '@jane/shared-ecomm/components';
import { useMyHigh } from '@jane/shared-ecomm/hooks';
import {
  useBrandSpecialsContext,
  useMenu,
  useSpecials,
} from '@jane/shared-ecomm/providers';
import { useShouldShowGold } from '@jane/shared/hooks';

import { useComposeMenuFilters } from '../../hooks/useComposeMenuFilters';
import { MenuFilters } from '../../MenuComponents/MenuFilters/MenuFilters';
import { MenuNavButtons } from '../../MenuComponents/MenuNavButtons/MenuNavButtons';
import { MenuProductsGrid } from '../../MenuComponents/MenuProductsGrid/MenuProductsGrid';
import { MenuQuickCheckoutRow } from '../../MenuComponents/MenuQuickCheckoutRow/MenuQuickCheckoutRow';
import { MenuPageSeo } from '../../MenuComponents/MenuSeo/MenuPageSeo';
import { MenuSponsoredCarousel } from '../../MenuComponents/MenuSponsoredCarousel/MenuSponsoredCarousel';
import { MenuTitle } from '../../MenuComponents/MenuTitle/MenuTitle';
import { MenuBrandSpecialsCarousel } from './MenuBrandSpecialsCarousel';
import { MenuCarousel } from './MenuCarousel';
import { MenuSpecialsCarousel } from './MenuSpecialsCarousel';

export const MenuFeatured = () => {
  const [queryParams] = useSearchParams();
  const hideSponsored = queryParams.get('hide_sponsored');
  const showQuickCheckoutRow = useFlag(FLAGS.ggOneClickCheckout);
  const isMobile = useMobileMediaQuery({});

  const { searchState } = useSearchContext();
  const {
    appInfo: { appMode },
    paths,
    store,
  } = useMenu();
  const shouldShowGold = useShouldShowGold({
    store: { id: store.id, state: store.state },
  });

  const { brandSpecials } = useBrandSpecialsContext();
  const staticFilters = useComposeMenuFilters([]);
  const isFilteringProducts =
    Object.values(searchState).filter(Boolean).length > 0;

  const searchFilter = buildSearchFilter({
    searchState,
    staticFilters,
  });

  const { facets: facetsData, isLoading: isLoadingFacets } = useFacets({
    searchFilter,
    searchQuery: searchState.searchText ?? undefined,
    storeId: Number(store.id),
  });

  const {
    products,
    indexName,
    isLoading,
    hasNextPage,
    numColumns,
    numHits,
    fetchNextPage,
  } = useMyHigh({
    maxProducts: !isFilteringProducts ? 0 : undefined,
    productsPerPage: isMobile
      ? MOBILE_PRODUCTS_PER_PAGE_GRID
      : DESKTOP_PRODUCTS_PER_PAGE_GRID,
    staticFilters,
  });

  const { placements, isLoading: isLoadingMultiPlacements } =
    useFetchMultiPlacements({
      storeId: Number(store.id),
    });

  const { specialsCards } = useSpecials();

  const showSpecialsRow =
    store.specials_row_configs?.enabled &&
    specialsCards?.some(
      (special) =>
        special.hidden_from_row === false && special.hidden_from_page === false
    );

  const showBrandSpecialsRow = shouldShowGold && brandSpecials?.length > 0;

  const renderedMultiPlacements =
    placements && !isLoadingMultiPlacements
      ? [
          ...(hideSponsored !== 'true'
            ? [
                <MenuSponsoredCarousel
                  key="sponsored"
                  menuCategory="featured"
                />,
              ]
            : []),
          ...placements.map((placement, index) => {
            if (placement.placement === Placement.BrandSpecials) {
              if (!showBrandSpecialsRow) {
                return null;
              }

              return (
                <MenuBrandSpecialsCarousel
                  key={`brand-specials-placement-${index}`}
                  title={placement.title}
                />
              );
            } else if (placement.placement === Placement.Specials) {
              if (!showSpecialsRow) {
                return null;
              }

              return (
                <MenuSpecialsCarousel
                  key={`specials-placement-${index}`}
                  title={placement.title}
                />
              );
            }

            return (
              <MenuCarousel
                key={`carousel-placement-${placement.placement}-${index}`}
                placement={placement}
                index={index}
              />
            );
          }),
        ]
      : isLoadingMultiPlacements
        ? Array.from({ length: 3 }, (_, i) => (
            <MenuProductCarousel key={`skeleton-${i}`} isLoading name="" />
          ))
        : null;

  const nextBreadcrumbs = [
    {
      title: 'Featured',
      url: paths.menuCategory('featured'),
    },
  ];

  const quickCheckoutRowEnabled =
    !!showQuickCheckoutRow && appMode === 'default';

  return (
    <Box pt={isMobile ? 0 : 24}>
      <MenuPageSeo title="featured" path={paths.menuCategory('featured')} />
      <MenuTitle px={isMobile ? 24 : 64} />
      <MenuFilters
        isLoadingProducts={isLoading}
        menuCategory="featured"
        searchResultFacets={facetsData}
        searchResultFacetsLoading={isLoadingFacets}
        totalResults={numHits}
        staticFilters={staticFilters}
      />
      {!isFilteringProducts ? (
        <Flex
          id="results"
          flexDirection="column"
          gap={isMobile ? 24 : 48}
          px={isMobile ? 24 : 64}
          pt={isFilteringProducts || isMobile ? 0 : 24}
        >
          {quickCheckoutRowEnabled && <MenuQuickCheckoutRow />}
          {renderedMultiPlacements}
          <MenuNavButtons isMobile={isMobile} />
        </Flex>
      ) : (
        <MenuProductsGrid
          breadcrumbs={nextBreadcrumbs}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          indexName={indexName}
          isLoading={isLoading}
          numColumns={numColumns}
          numHits={numHits}
          pageTitle="featured"
          products={products}
        />
      )}
    </Box>
  );
};
